/**
 * <Can> — Gate declarativo basado en permisos dinámicos (RBAC Fase A).
 *
 * Muestra su contenido cuando el usuario tiene el/los permiso(s) requeridos
 * en el contexto de club opcional. Diseñado para reemplazar chequeos
 * imperativos del tipo `if (role === "director") return <>...`.
 *
 * Ejemplos:
 *
 *   <Can permission="finance.write" clubId={clubId}>
 *     <button>Registrar pago</button>
 *   </Can>
 *
 *   <Can anyOf={["events.write", "events.approve"]} clubId={clubId}
 *        fallback={<p>Sólo lectura</p>}>
 *     <EventForm />
 *   </Can>
 *
 *   <Can allOf={["reports.read", "reports.export"]}>
 *     <ExportButton />
 *   </Can>
 *
 * Reglas de precedencia si se pasan varios props: `permission` > `allOf` >
 * `anyOf`.
 */
import type { ReactNode } from "react";
import { useEffectivePermissions } from "@/hooks/useEffectivePermissions";

interface CanProps {
  /** Permiso único requerido. */
  permission?: string;
  /** Requiere que el user tenga TODOS estos permisos. */
  allOf?: string[];
  /** Requiere que el user tenga al menos UNO. */
  anyOf?: string[];
  /** Contexto de club para aplicar overrides. */
  clubId?: string;
  /** Contenido a mostrar cuando pasa el chequeo. */
  children: ReactNode;
  /** Contenido alternativo cuando NO pasa. Por defecto, no se renderiza nada. */
  fallback?: ReactNode;
  /**
   * Si es true, mientras el hook aún está cargando renderiza `fallback` (o
   * nada). Por defecto: mientras carga, tratamos como "sin permiso" para
   * evitar mostrar UI privilegiada de forma transitoria.
   */
  showWhileLoading?: boolean;
}

export function Can({
  permission,
  allOf,
  anyOf,
  clubId,
  children,
  fallback = null,
  showWhileLoading = false,
}: CanProps) {
  const { loading, can, canAll, canAny } = useEffectivePermissions(clubId);

  if (loading && !showWhileLoading) return <>{fallback}</>;

  let allowed = false;
  if (permission) {
    allowed = can(permission);
  } else if (allOf && allOf.length > 0) {
    allowed = canAll(allOf);
  } else if (anyOf && anyOf.length > 0) {
    allowed = canAny(anyOf);
  } else {
    // Sin criterio: por seguridad, denegar.
    allowed = false;
  }

  return <>{allowed ? children : fallback}</>;
}
